/**
 * VoyageHeader.js
 *
 * Redesigned voyage log header - maritime logbook style.
 * Shows all captured voyage data in structured sections.
 */

import React, { useState, useEffect } from 'react';
import { Ship, MapPin, User, Users, Clock, Edit2, Lock, Wind, Waves, Eye, Fuel, ArrowLeft } from 'lucide-react';
import FuelGaugeBar from './FuelGaugeBar';
import { VOYAGE_STATUS } from '../../../../../config/operationsConfig';
import { portLabel } from '../../../../../utils/voyage/portLabel';

/**
 * Format timestamp for display
 */
const formatTime = (timestamp) => {
  if (!timestamp) return '---';
  try {
    return new Date(timestamp).toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    });
  } catch {
    return '---';
  }
};

const formatDate = (timestamp) => {
  if (!timestamp) return '---';
  try {
    return new Date(timestamp).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  } catch {
    return '---';
  }
};

/**
 * Format Beaufort scale for display
 */
const formatWindSpeed = (speed) => {
  if (!speed) return '---';
  // If it's already a string like "F4" or "Force 4", return as is
  if (typeof speed === 'string') return speed;
  return `F${speed}`;
};

/**
 * Format sea state for display
 */
const formatSeaState = (state) => {
  if (!state) return '---';
  const stateMap = {
    'calm': 'Calm',
    'smooth': 'Smooth',
    'slight': 'Slight',
    'moderate': 'Moderate',
    'rough': 'Rough',
    'very_rough': 'Very Rough',
    'high': 'High'
  };
  return stateMap[state] || state;
};

/**
 * Format visibility for display
 */
const formatVisibility = (vis) => {
  if (!vis) return '---';
  const visMap = {
    'fog': 'Fog (<1nm)',
    'poor': 'Poor (1-2nm)',
    'moderate': 'Moderate (2-5nm)',
    'good': 'Good (>5nm)'
  };
  return visMap[vis] || vis;
};

const VoyageHeader = ({
  voyage,
  vessel,
  onEdit,
  onBack,  // Handler to exit full-screen voyage view
  isReadOnly = false
}) => {
  // Elapsed time state and effect
  const [elapsedTime, setElapsedTime] = useState('');

  useEffect(() => {
    if (!voyage?.departureTime || voyage?.arrivalTime) return;

    const updateElapsed = () => {
      const start = new Date(voyage.departureTime);
      const now = new Date();
      const diff = now - start;

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setElapsedTime(`${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`);
    };

    updateElapsed();
    const interval = setInterval(updateElapsed, 1000); // Update every second for live timer

    return () => clearInterval(interval);
  }, [voyage?.departureTime, voyage?.arrivalTime]);

  if (!voyage) {
    return (
      <div className="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden">
        <div className="bg-gray-900 print:bg-white print:text-black px-5 py-4">
          <div className="flex items-center gap-3">
            <Ship className="h-5 w-5 text-cyan-400 print:text-cyan-600" />
            <h2 className="text-lg font-semibold text-white print:text-black">No Active Voyage</h2>
          </div>
        </div>
        <div className="px-6 py-5">
          <p className="text-slate-500">No voyage data available</p>
        </div>
      </div>
    );
  }

  // Extract weather data
  const weather = voyage.departureWeather || voyage.weather || {};

  // Calculate total POB
  const crewCount = voyage.crewCount || 0;
  const passengerCount = voyage.passengerCount || 0;
  const totalPOB = voyage.personsOnBoard || (crewCount + passengerCount) || 0;

  // Get purpose display
  const purposeMap = {
    'crew_transfer': 'Crew Transfer',
    'survey': 'Survey',
    'passenger_run': 'Passenger Run',
    'cargo': 'Cargo',
    'training': 'Training',
    'other': 'Other'
  };
  const purposeDisplay = voyage.purpose === 'other' && voyage.purposeOther
    ? voyage.purposeOther
    : purposeMap[voyage.purpose] || voyage.purpose || null;

  return (
    <div className="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden">

      {/* Dark Header Band */}
      <div className="bg-gray-900 print:bg-white print:text-black px-5 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Back button - exits full-screen mode */}
            {onBack && (
              <button
                onClick={onBack}
                className="p-2 -ml-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors print:hidden"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
            )}
            <div>
              <p className="text-xs uppercase tracking-wider text-slate-400 print:text-slate-600">Voyage Log</p>
              <div className="flex items-center gap-3 mt-1 flex-wrap">
                <span className="font-mono text-lg font-semibold text-white print:text-black">
                  {voyage.voyageNumber || `VOY-${voyage.id?.slice(-6) || '---'}`}
                </span>
                {purposeDisplay && (
                  <>
                    <span className="text-slate-500 print:text-slate-400">•</span>
                    <span className="text-cyan-400 print:text-cyan-600">{purposeDisplay}</span>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="text-right">
            {/* Status badge - prominent with pulsing indicator */}
            <div className="flex items-center gap-2 justify-end">
              {voyage.status === VOYAGE_STATUS.UNDERWAY ? (
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-cyan-600 print:bg-cyan-100">
                  <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                  <span className="text-xs font-semibold uppercase text-white print:text-cyan-900">Underway</span>
                </div>
              ) : voyage.status === VOYAGE_STATUS.PRE_DEPARTURE ? (
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-600 print:bg-amber-100">
                  <span className="text-xs font-semibold uppercase text-white print:text-amber-900">Pre-Departure</span>
                </div>
              ) : isReadOnly ? (
                <>
                  <Lock className="h-3 w-3 text-slate-400 print:text-slate-600" />
                  <span className="text-xs uppercase text-slate-400 print:text-slate-600">In Progress</span>
                </>
              ) : null}
            </div>

            {/* Edit button */}
            {!isReadOnly && onEdit && (
              <button
                onClick={onEdit}
                className="mt-2 inline-flex items-center gap-2 rounded-lg border border-white/20 bg-white/10 px-3 py-1.5 text-sm font-medium text-white hover:bg-white/20 transition-colors print:hidden"
              >
                <Edit2 className="h-4 w-4" />
                <span>Edit</span>
              </button>
            )}
          </div>
        </div>

        {/* Stats strip at bottom of header (when UNDERWAY) */}
        {voyage.status === VOYAGE_STATUS.UNDERWAY && (
          <div className="mt-4 pt-4 border-t border-white/10 print:border-slate-200 flex items-center justify-between text-sm">
            {/* POB count - left */}
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-cyan-400 print:text-cyan-600" />
              <span className="text-slate-400 print:text-slate-600">POB:</span>
              <span className="font-semibold text-white print:text-black">{totalPOB}</span>
            </div>

            {/* Fuel % with color coding - center */}
            {voyage.currentFuel !== undefined && voyage.currentFuel !== null && (
              <div className="flex items-center gap-2">
                <Fuel className={`h-4 w-4 ${voyage.currentFuel <= 25 ? 'text-red-500' : 'text-cyan-400 print:text-cyan-600'}`} />
                <span className="text-slate-400 print:text-slate-600">Fuel:</span>
                <span className={`font-semibold ${voyage.currentFuel <= 25 ? 'text-red-400 print:text-red-600' : 'text-white print:text-black'}`}>
                  {voyage.currentFuel}%
                </span>
              </div>
            )}

            {/* Elapsed time - right */}
            {elapsedTime && (
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-cyan-400 print:text-cyan-600" />
                <span className="text-slate-400 print:text-slate-600">Elapsed:</span>
                <span className="font-mono font-semibold text-white print:text-black">{elapsedTime}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Content Area */}
      <div className="p-4 space-y-4">

        {/* Route Section */}
        <div className="border border-slate-200 rounded-lg p-4">
          <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-3">Route</p>

          {/* Desktop: horizontal layout */}
          <div className="hidden sm:flex items-center justify-between">
            <div className="text-center flex-1">
              <div className="w-3 h-3 border-2 border-green-500 bg-green-100 rounded-full mx-auto"></div>
              <p className="font-medium text-slate-800 mt-2">
                {voyage.departure || portLabel(voyage.from, '---')}
              </p>
              <p className="text-sm text-slate-500">
                Dep {formatTime(voyage.departureTime)}
              </p>
            </div>

            <div className="flex-1 mx-4 border-t-2 border-dashed border-slate-300 relative">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-2 text-slate-400">▶</div>
            </div>

            <div className="text-center flex-1">
              <div className="w-3 h-3 bg-cyan-500 rounded-full mx-auto"></div>
              <p className="font-medium text-slate-800 mt-2">
                {voyage.destination || portLabel(voyage.to, '---')}
              </p>
              <p className="text-sm text-slate-500">
                {voyage.eta ? `ETA ${formatTime(voyage.eta)}` : '---'}
              </p>
            </div>
          </div>

          {/* Mobile: vertical layout */}
          <div className="sm:hidden space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 border-2 border-green-500 bg-green-100 rounded-full flex-shrink-0"></div>
              <div>
                <p className="font-medium text-slate-800">
                  {voyage.departure || portLabel(voyage.from, '---')}
                </p>
                <p className="text-sm text-slate-500">Dep {formatTime(voyage.departureTime)}</p>
              </div>
            </div>
            <div className="ml-1.5 border-l-2 border-dashed border-slate-300 h-4"></div>
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 bg-cyan-500 rounded-full flex-shrink-0"></div>
              <div>
                <p className="font-medium text-slate-800">
                  {voyage.destination || portLabel(voyage.to, '---')}
                </p>
                <p className="text-sm text-slate-500">
                  {voyage.eta ? `ETA ${formatTime(voyage.eta)}` : '---'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Two-column grid: Personnel & Conditions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

          {/* Personnel Section */}
          <div className="border border-slate-200 rounded-lg p-4">
            <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-3">Personnel</p>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-slate-500 flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Master
                </dt>
                <dd className="font-medium text-slate-800">{voyage.masterDesignation?.masterName || voyage.master || '---'}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-slate-500">Crew</dt>
                <dd className="font-medium text-slate-800">{crewCount || '---'}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-slate-500">Passengers</dt>
                <dd className="font-medium text-slate-800">{passengerCount || '---'}</dd>
              </div>
              <div className="flex justify-between border-t border-slate-100 pt-2 mt-2">
                <dt className="font-medium text-slate-700 flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Total POB
                </dt>
                <dd className="font-semibold text-slate-800">{totalPOB}</dd>
              </div>
            </dl>
          </div>

          {/* Conditions Section */}
          <div className="border border-slate-200 rounded-lg p-4">
            <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-3">Conditions at Departure</p>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-slate-500 flex items-center gap-2">
                  <Wind className="h-4 w-4" />
                  Wind
                </dt>
                <dd className="font-medium text-slate-800">
                  {weather.windDirection || '---'} {formatWindSpeed(weather.windSpeed || weather.windForce)}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-slate-500 flex items-center gap-2">
                  <Waves className="h-4 w-4" />
                  Sea State
                </dt>
                <dd className="font-medium text-slate-800">{formatSeaState(weather.seaState)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-slate-500 flex items-center gap-2">
                  <Eye className="h-4 w-4" />
                  Visibility
                </dt>
                <dd className="font-medium text-slate-800">{formatVisibility(weather.visibility)}</dd>
              </div>
              <div className="flex justify-between border-t border-slate-100 pt-2 mt-2">
                <dt className="font-medium text-slate-700 flex items-center gap-2">
                  <Fuel className="h-4 w-4" />
                  Departure Fuel
                </dt>
                <dd className="font-bold text-slate-800">{voyage.departureFuel ? `${voyage.departureFuel}%` : '---'}</dd>
              </div>
            </dl>
          </div>
        </div>

        {/* Current Fuel Gauge - if tracking fuel */}
        {voyage.departureFuel !== undefined && voyage.departureFuel !== null && voyage.currentFuel !== undefined && (
          <div className="border border-slate-200 rounded-lg p-4">
            <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-3">Current Fuel Status</p>
            <FuelGaugeBar
              departureFuel={voyage.departureFuel}
              currentFuel={voyage.currentFuel || voyage.arrivalFuel}
              compact={false}
            />
          </div>
        )}

      </div>
    </div>
  );
};

export default VoyageHeader;
